import { create } from 'zustand'

// Animatable settings for day/night cycle (lighting + materials)
export interface AnimatableSettings {
  ambientIntensity: number
  directionalIntensity: number
  directionalAzimuth: number
  directionalElevation: number
  hemisphereIntensity: number
  hemisphereSkyColor: string
  hemisphereGroundColor: string
  backgroundTopColor: string
  backgroundBottomColor: string
  shadowOpacity: number
  materialRoughness: number
  materialMetalness: number
}

// Keys of animatable settings for iteration
export const ANIMATABLE_KEYS: (keyof AnimatableSettings)[] = [
  'ambientIntensity',
  'directionalIntensity',
  'directionalAzimuth',
  'directionalElevation',
  'hemisphereIntensity',
  'hemisphereSkyColor',
  'hemisphereGroundColor',
  'backgroundTopColor',
  'backgroundBottomColor',
  'shadowOpacity',
  'materialRoughness',
  'materialMetalness',
]

interface DevModeState {
  // Ambient light
  ambientIntensity: number

  // Directional light
  directionalIntensity: number
  directionalAzimuth: number // horizontal rotation in degrees (0-360)
  directionalElevation: number // vertical angle in degrees (0-90)

  // Hemisphere light
  hemisphereIntensity: number
  hemisphereSkyColor: string
  hemisphereGroundColor: string

  // Background gradient
  backgroundTopColor: string
  backgroundBottomColor: string

  // Camera controls
  focalLength: number // in mm (35mm full-frame equivalent)
  dampingFactor: number
  rotateSpeed: number
  panSpeed: number
  zoomSpeed: number

  // Shadow settings
  shadowType: 'contact' | 'directional'
  shadowOpacity: number
  shadowBlur: number
  shadowResolution: number
  // Directional shadow map settings
  shadowMapSize: number
  shadowBias: number
  shadowNormalBias: number
  shadowCameraSize: number
  shadowRadius: number // PCF soft shadow blur radius

  // Material settings
  materialRoughness: number
  materialMetalness: number

  // Auto-rotate
  autoRotate: boolean
  autoRotateSpeed: number

  // POI Markers
  poiMarkerScale: number
  poiMarkerOpacity: number
  poiMarkerColor: string
  poiMarkerOutlineColor: string
  poiMarkerOutlineWidth: number

  // Day/Night Cycle
  dayNightPlaying: boolean
  dayNightProgress: number // 0-1 (0=day, 0.5=night, 1=back to day)
  dayNightDuration: number // seconds for full cycle
  dayNightLerpMode: 'smooth' | 'threshold'
  dayNightThresholdSteps: number
  dawnPreset: AnimatableSettings
  dayPreset: AnimatableSettings
  duskPreset: AnimatableSettings
  nightPreset: AnimatableSettings
  defaultPreset: AnimatableSettings
  activePreview: 'dawn' | 'day' | 'dusk' | 'night' | 'default' | null
  presetsDirty: boolean // true when presets have unsaved changes
  presetsSaving: boolean // true while save is in flight

  // Setters - Lighting
  setAmbientIntensity: (value: number) => void
  setDirectionalIntensity: (value: number) => void
  setDirectionalAzimuth: (value: number) => void
  setDirectionalElevation: (value: number) => void
  setHemisphereIntensity: (value: number) => void
  setHemisphereSkyColor: (color: string) => void
  setHemisphereGroundColor: (color: string) => void
  setBackgroundTopColor: (color: string) => void
  setBackgroundBottomColor: (color: string) => void

  // Setters - Camera
  setFocalLength: (value: number) => void
  setDampingFactor: (value: number) => void
  setRotateSpeed: (value: number) => void
  setPanSpeed: (value: number) => void
  setZoomSpeed: (value: number) => void

  // Setters - Shadows
  setShadowType: (value: 'contact' | 'directional') => void
  setShadowOpacity: (value: number) => void
  setShadowBlur: (value: number) => void
  setShadowResolution: (value: number) => void
  setShadowMapSize: (value: number) => void
  setShadowBias: (value: number) => void
  setShadowNormalBias: (value: number) => void
  setShadowCameraSize: (value: number) => void
  setShadowRadius: (value: number) => void

  // Setters - Materials
  setMaterialRoughness: (value: number) => void
  setMaterialMetalness: (value: number) => void

  // Setters - Auto-rotate
  setAutoRotate: (value: boolean) => void
  setAutoRotateSpeed: (value: number) => void

  // Setters - POI Markers
  setPoiMarkerScale: (value: number) => void
  setPoiMarkerOpacity: (value: number) => void
  setPoiMarkerColor: (value: string) => void
  setPoiMarkerOutlineColor: (value: string) => void
  setPoiMarkerOutlineWidth: (value: number) => void

  // Day/Night Cycle Actions
  setDawnPresetValue: (key: keyof AnimatableSettings, value: number | string) => void
  setDayPresetValue: (key: keyof AnimatableSettings, value: number | string) => void
  setDuskPresetValue: (key: keyof AnimatableSettings, value: number | string) => void
  setNightPresetValue: (key: keyof AnimatableSettings, value: number | string) => void
  setDefaultPresetValue: (key: keyof AnimatableSettings, value: number | string) => void
  previewPreset: (which: 'dawn' | 'day' | 'dusk' | 'night' | 'default') => void
  startDayNight: () => void
  stopDayNight: () => void
  setDayNightProgress: (value: number) => void
  setDayNightDuration: (value: number) => void
  setDayNightLerpMode: (value: 'smooth' | 'threshold') => void
  setDayNightThresholdSteps: (value: number) => void
  applyAnimatedValues: (values: Partial<AnimatableSettings>) => void
  resetPresetsToDefaults: () => void
  savePresets: () => Promise<void>
  loadPresets: () => Promise<void>

  // Reset
  resetToDefaults: () => void
}

const DEFAULTS = {
  // Lighting
  ambientIntensity: 0.3,
  directionalIntensity: 1.1,
  directionalAzimuth: 215, // degrees (0 = front, 90 = right, 180 = back, 270 = left)
  directionalElevation: 55, // degrees (0 = horizon, 90 = directly above)
  hemisphereIntensity: 0.5,
  hemisphereSkyColor: '#87CEEB',
  hemisphereGroundColor: '#FFE4E1',
  backgroundTopColor: '#E6F3FF',
  backgroundBottomColor: '#FFF0F5',

  // Camera
  focalLength: 24, // mm (35mm equivalent, ~50° FOV)
  dampingFactor: 0.08,
  rotateSpeed: 0.5,
  panSpeed: 1.0,
  zoomSpeed: 1.0,

  // Shadows
  shadowType: 'directional' as const,
  shadowOpacity: 0.1,
  shadowBlur: 2,
  shadowResolution: 256,
  // Directional shadow map
  shadowMapSize: 4096,
  shadowBias: -0.0001,
  shadowNormalBias: 0.5,
  shadowCameraSize: 150,
  shadowRadius: 2, // PCF soft shadow blur

  // Materials
  materialRoughness: 0.7,
  materialMetalness: 0.1,

  // Auto-rotate
  autoRotate: false,
  autoRotateSpeed: 0.5,

  // POI Markers
  poiMarkerScale: 0.6,
  poiMarkerOpacity: 0.8,
  poiMarkerColor: '#666666',
  poiMarkerOutlineColor: '#ffffff',
  poiMarkerOutlineWidth: 1,

  // Day/Night Cycle
  dayNightPlaying: false,
  dayNightProgress: 0,
  dayNightDuration: 10,
  dayNightLerpMode: 'smooth' as const,
  dayNightThresholdSteps: 4,
}

// Default day preset
export const DAY_PRESET_DEFAULTS: AnimatableSettings = {
  ambientIntensity: 0.6,
  directionalIntensity: 0.8,
  directionalAzimuth: 45,
  directionalElevation: 55,
  hemisphereIntensity: 0.4,
  hemisphereSkyColor: '#87CEEB',
  hemisphereGroundColor: '#FFE4E1',
  backgroundTopColor: '#E6F3FF',
  backgroundBottomColor: '#FFF0F5',
  shadowOpacity: 0.4,
  materialRoughness: 0.7,
  materialMetalness: 0.1,
}

// Default dawn preset (sunrise transition)
export const DAWN_PRESET_DEFAULTS: AnimatableSettings = {
  ambientIntensity: 0.35,
  directionalIntensity: 0.45,
  directionalAzimuth: 90,
  directionalElevation: 10,
  hemisphereIntensity: 0.25,
  hemisphereSkyColor: '#FF9E80',
  hemisphereGroundColor: '#4A3728',
  backgroundTopColor: '#FFB74D',
  backgroundBottomColor: '#E65100',
  shadowOpacity: 0.25,
  materialRoughness: 0.75,
  materialMetalness: 0.12,
}

// Default night preset
export const NIGHT_PRESET_DEFAULTS: AnimatableSettings = {
  ambientIntensity: 0.2,
  directionalIntensity: 0.2,
  directionalAzimuth: 225,
  directionalElevation: 15,
  hemisphereIntensity: 0.1,
  hemisphereSkyColor: '#1a1a2e',
  hemisphereGroundColor: '#2d1f3d',
  backgroundTopColor: '#0a0a1a',
  backgroundBottomColor: '#1a1a2e',
  shadowOpacity: 0.6,
  materialRoughness: 0.8,
  materialMetalness: 0.15,
}

// Default dusk preset (sunset transition)
export const DUSK_PRESET_DEFAULTS: AnimatableSettings = {
  ambientIntensity: 0.35,
  directionalIntensity: 0.45,
  directionalAzimuth: 270,
  directionalElevation: 10,
  hemisphereIntensity: 0.25,
  hemisphereSkyColor: '#FF6F61',
  hemisphereGroundColor: '#3E2723',
  backgroundTopColor: '#E64A19',
  backgroundBottomColor: '#311B92',
  shadowOpacity: 0.35,
  materialRoughness: 0.75,
  materialMetalness: 0.12,
}

// Default animatable settings (from DEFAULTS, used as initial "Default" column)
export const DEFAULT_PRESET_DEFAULTS: AnimatableSettings = {
  ambientIntensity: DEFAULTS.ambientIntensity,
  directionalIntensity: DEFAULTS.directionalIntensity,
  directionalAzimuth: DEFAULTS.directionalAzimuth,
  directionalElevation: DEFAULTS.directionalElevation,
  hemisphereIntensity: DEFAULTS.hemisphereIntensity,
  hemisphereSkyColor: DEFAULTS.hemisphereSkyColor,
  hemisphereGroundColor: DEFAULTS.hemisphereGroundColor,
  backgroundTopColor: DEFAULTS.backgroundTopColor,
  backgroundBottomColor: DEFAULTS.backgroundBottomColor,
  shadowOpacity: DEFAULTS.shadowOpacity,
  materialRoughness: DEFAULTS.materialRoughness,
  materialMetalness: DEFAULTS.materialMetalness,
}

const STATIC_URL = '/data/lighting-presets.json'

export const useDevModeStore = create<DevModeState>((set, get) => ({
  // Initial state
  ...DEFAULTS,
  dawnPreset: { ...DAWN_PRESET_DEFAULTS },
  dayPreset: { ...DAY_PRESET_DEFAULTS },
  duskPreset: { ...DUSK_PRESET_DEFAULTS },
  nightPreset: { ...NIGHT_PRESET_DEFAULTS },
  defaultPreset: { ...DEFAULT_PRESET_DEFAULTS },
  activePreview: null,
  presetsDirty: false,
  presetsSaving: false,

  // Setters - Lighting
  setAmbientIntensity: (value) => set({ ambientIntensity: value }),
  setDirectionalIntensity: (value) => set({ directionalIntensity: value }),
  setDirectionalAzimuth: (value) => set({ directionalAzimuth: value }),
  setDirectionalElevation: (value) => set({ directionalElevation: value }),
  setHemisphereIntensity: (value) => set({ hemisphereIntensity: value }),
  setHemisphereSkyColor: (color) => set({ hemisphereSkyColor: color }),
  setHemisphereGroundColor: (color) => set({ hemisphereGroundColor: color }),
  setBackgroundTopColor: (color) => set({ backgroundTopColor: color }),
  setBackgroundBottomColor: (color) => set({ backgroundBottomColor: color }),

  // Setters - Camera
  setFocalLength: (value) => set({ focalLength: value }),
  setDampingFactor: (value) => set({ dampingFactor: value }),
  setRotateSpeed: (value) => set({ rotateSpeed: value }),
  setPanSpeed: (value) => set({ panSpeed: value }),
  setZoomSpeed: (value) => set({ zoomSpeed: value }),

  // Setters - Shadows
  setShadowType: (value) => set({ shadowType: value }),
  setShadowOpacity: (value) => set({ shadowOpacity: value }),
  setShadowBlur: (value) => set({ shadowBlur: value }),
  setShadowResolution: (value) => set({ shadowResolution: value }),
  setShadowMapSize: (value) => set({ shadowMapSize: value }),
  setShadowBias: (value) => set({ shadowBias: value }),
  setShadowNormalBias: (value) => set({ shadowNormalBias: value }),
  setShadowCameraSize: (value) => set({ shadowCameraSize: value }),
  setShadowRadius: (value) => set({ shadowRadius: value }),

  // Setters - Materials
  setMaterialRoughness: (value) => set({ materialRoughness: value }),
  setMaterialMetalness: (value) => set({ materialMetalness: value }),

  // Setters - Auto-rotate
  setAutoRotate: (value) => set({ autoRotate: value }),
  setAutoRotateSpeed: (value) => set({ autoRotateSpeed: value }),

  // Setters - POI Markers
  setPoiMarkerScale: (value) => set({ poiMarkerScale: value }),
  setPoiMarkerOpacity: (value) => set({ poiMarkerOpacity: value }),
  setPoiMarkerColor: (value) => set({ poiMarkerColor: value }),
  setPoiMarkerOutlineColor: (value) => set({ poiMarkerOutlineColor: value }),
  setPoiMarkerOutlineWidth: (value) => set({ poiMarkerOutlineWidth: value }),

  // Day/Night Preset Actions
  setDawnPresetValue: (key, value) => {
    const state = get()
    const updated: Record<string, unknown> = {
      dawnPreset: { ...state.dawnPreset, [key]: value },
      presetsDirty: true,
    }
    if (state.activePreview === 'dawn') {
      updated[key] = value
    }
    set(updated as Partial<DevModeState>)
  },
  setDayPresetValue: (key, value) => {
    const state = get()
    const updated: Record<string, unknown> = {
      dayPreset: { ...state.dayPreset, [key]: value },
      presetsDirty: true,
    }
    if (state.activePreview === 'day') {
      updated[key] = value
    }
    set(updated as Partial<DevModeState>)
  },
  setDuskPresetValue: (key, value) => {
    const state = get()
    const updated: Record<string, unknown> = {
      duskPreset: { ...state.duskPreset, [key]: value },
      presetsDirty: true,
    }
    if (state.activePreview === 'dusk') {
      updated[key] = value
    }
    set(updated as Partial<DevModeState>)
  },
  setNightPresetValue: (key, value) => {
    const state = get()
    const updated: Record<string, unknown> = {
      nightPreset: { ...state.nightPreset, [key]: value },
      presetsDirty: true,
    }
    if (state.activePreview === 'night') {
      updated[key] = value
    }
    set(updated as Partial<DevModeState>)
  },
  setDefaultPresetValue: (key, value) => {
    const state = get()
    const updated: Record<string, unknown> = {
      defaultPreset: { ...state.defaultPreset, [key]: value },
      presetsDirty: true,
    }
    if (state.activePreview === 'default') {
      updated[key] = value
    }
    set(updated as Partial<DevModeState>)
  },
  previewPreset: (which) => {
    const state = get()
    if (state.dayNightPlaying) {
      set({ dayNightPlaying: false })
    }
    let preset: AnimatableSettings
    if (which === 'dawn') {
      preset = state.dawnPreset
    } else if (which === 'day') {
      preset = state.dayPreset
    } else if (which === 'dusk') {
      preset = state.duskPreset
    } else if (which === 'night') {
      preset = state.nightPreset
    } else {
      preset = state.defaultPreset
    }
    set({ ...preset, activePreview: which })
  },
  startDayNight: () => {
    set({ dayNightPlaying: true, dayNightProgress: 0, activePreview: null })
  },
  stopDayNight: () => {
    const { defaultPreset } = get()
    set({ dayNightPlaying: false, ...defaultPreset, activePreview: null })
  },
  setDayNightProgress: (value) => set({ dayNightProgress: value }),
  setDayNightDuration: (value) => set({ dayNightDuration: value }),
  setDayNightLerpMode: (value) => set({ dayNightLerpMode: value }),
  setDayNightThresholdSteps: (value) => set({ dayNightThresholdSteps: value }),
  applyAnimatedValues: (values) => set(values),
  resetPresetsToDefaults: () =>
    set({
      dawnPreset: { ...DAWN_PRESET_DEFAULTS },
      dayPreset: { ...DAY_PRESET_DEFAULTS },
      duskPreset: { ...DUSK_PRESET_DEFAULTS },
      nightPreset: { ...NIGHT_PRESET_DEFAULTS },
      defaultPreset: { ...DEFAULT_PRESET_DEFAULTS },
      presetsDirty: true,
    }),

  savePresets: async () => {
    set({ presetsSaving: true })
    console.log('[DevModeStore] Save disabled in static mode')
    set({ presetsDirty: false, presetsSaving: false })
  },

  loadPresets: async () => {
    try {
      const res = await fetch(STATIC_URL)
      if (!res.ok) throw new Error('Load failed')
      const data = await res.json()
      const hasAllKeys = (obj: Record<string, unknown>) => ANIMATABLE_KEYS.every((k) => k in obj)
      const updates: Partial<DevModeState> = {}
      if (data.dawnPreset && hasAllKeys(data.dawnPreset)) {
        updates.dawnPreset = data.dawnPreset
      }
      if (data.dayPreset && hasAllKeys(data.dayPreset)) {
        updates.dayPreset = data.dayPreset
      }
      if (data.duskPreset && hasAllKeys(data.duskPreset)) {
        updates.duskPreset = data.duskPreset
      }
      if (data.nightPreset && hasAllKeys(data.nightPreset)) {
        updates.nightPreset = data.nightPreset
      }
      if (data.defaultPreset && hasAllKeys(data.defaultPreset)) {
        updates.defaultPreset = data.defaultPreset
        // Apply default preset as the initial scene state
        Object.assign(updates, data.defaultPreset)
      }
      // Day/night cycle
      if (data.dayNightDuration != null) updates.dayNightDuration = data.dayNightDuration
      if (data.dayNightLerpMode) updates.dayNightLerpMode = data.dayNightLerpMode
      if (data.dayNightThresholdSteps != null)
        updates.dayNightThresholdSteps = data.dayNightThresholdSteps
      // Camera
      if (data.focalLength != null) updates.focalLength = data.focalLength
      if (data.dampingFactor != null) updates.dampingFactor = data.dampingFactor
      if (data.rotateSpeed != null) updates.rotateSpeed = data.rotateSpeed
      if (data.panSpeed != null) updates.panSpeed = data.panSpeed
      if (data.zoomSpeed != null) updates.zoomSpeed = data.zoomSpeed
      // Shadows
      if (data.shadowType) updates.shadowType = data.shadowType
      if (data.shadowOpacity != null) updates.shadowOpacity = data.shadowOpacity
      if (data.shadowBlur != null) updates.shadowBlur = data.shadowBlur
      if (data.shadowResolution != null) updates.shadowResolution = data.shadowResolution
      if (data.shadowMapSize != null) updates.shadowMapSize = data.shadowMapSize
      if (data.shadowBias != null) updates.shadowBias = data.shadowBias
      if (data.shadowNormalBias != null) updates.shadowNormalBias = data.shadowNormalBias
      if (data.shadowCameraSize != null) updates.shadowCameraSize = data.shadowCameraSize
      if (data.shadowRadius != null) updates.shadowRadius = data.shadowRadius
      // Auto-rotate
      if (data.autoRotate != null) updates.autoRotate = data.autoRotate
      if (data.autoRotateSpeed != null) updates.autoRotateSpeed = data.autoRotateSpeed
      // POI markers
      if (data.poiMarkerScale != null) updates.poiMarkerScale = data.poiMarkerScale
      if (data.poiMarkerOpacity != null) updates.poiMarkerOpacity = data.poiMarkerOpacity
      if (data.poiMarkerColor != null) updates.poiMarkerColor = data.poiMarkerColor
      if (data.poiMarkerOutlineColor != null) updates.poiMarkerOutlineColor = data.poiMarkerOutlineColor
      if (data.poiMarkerOutlineWidth != null) updates.poiMarkerOutlineWidth = data.poiMarkerOutlineWidth

      updates.presetsDirty = false
      set(updates)
    } catch (error) {
      console.error('Failed to load lighting presets:', error)
    }
  },

  // Reset all to defaults
  resetToDefaults: () =>
    set({
      ...DEFAULTS,
      dawnPreset: { ...DAWN_PRESET_DEFAULTS },
      dayPreset: { ...DAY_PRESET_DEFAULTS },
      duskPreset: { ...DUSK_PRESET_DEFAULTS },
      nightPreset: { ...NIGHT_PRESET_DEFAULTS },
      defaultPreset: { ...DEFAULT_PRESET_DEFAULTS },
      presetsDirty: true,
    }),
}))
